import { FC, useMemo, useState } from 'react'

import { useSuspenseQuery } from '@tanstack/react-query'

import {
  Alert,
  Avatar,
  AvatarFallback,
  Button,
  Card,
  Chip,
  Cluster,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Icon,
  Select,
  Stack,
  Typography,
  useToaster,
} from '@forto-dev/phoenix-ui-core'
import { Layout } from '@forto-dev/phoenix-ui-core/ship'

import { useAuth } from '@/features/auth'
import { Access, authorize } from '@/features/auth'
import { Footer } from '@/fragments/footer'
import { Header } from '@/fragments/header'
import { Navigation } from '@/fragments/navigation'
import { getInitialsForName } from '@/utils/initials'
import { useLocalisation } from '@/utils/localisation'

import { CreateUserDialog } from './create'
import { EditUserDialog } from './edit'
import { organizationsQueryOptions } from '../api/master-data.service'
import { usersQueryOptions } from '../api/users.service'
import { useSendInvitation } from '../api/useSendInvitation'
import { useUserDelete } from '../api/useUserDelete'
import { isValidRole, roles, User } from '../domain/user'

export const Users: FC = () => {
  const { translate } = useLocalisation()
  const { user: authUser, customer } = useAuth()
  const toaster = useToaster()
  const invitationMutation = useSendInvitation()
  const deleteUserMutation = useUserDelete()
  const { data: users } = useSuspenseQuery(usersQueryOptions())
  const { data: organizations } = useSuspenseQuery(organizationsQueryOptions(customer?._id || ''))
  const [showCreateDialog, setShowCreateDialog] = useState<boolean>(false)
  const [currentDeleteUser, setCurrentDeleteUser] = useState<User | undefined>(undefined)
  const [currentEditUser, setCurrentEditUser] = useState<User | undefined>(undefined)
  const [currentRoleFilter, setCurrentRoleFilter] = useState<string>('all')
  const [currentOrganizationFilter, setCurrentOrganizationFilter] = useState<string>('all')
  const filteredUsers = useMemo(() => {
    return users.filter((user) => {
      if (currentRoleFilter !== 'all' && isValidRole(currentRoleFilter) && !user.roles.includes(currentRoleFilter)) {
        return false
      }

      if (currentOrganizationFilter !== 'all' && user.organizationId !== currentOrganizationFilter) {
        return false
      }

      return true
    })
  }, [currentOrganizationFilter, currentRoleFilter, users])

  return (
    <>
      <Layout
        header={<Header />}
        navigation={<Navigation />}
        title={
          <Cluster gap={8} x="between" y="center">
            <Typography variant="h4">{translate('users.title')}</Typography>
            <Access roles={authorize.admins}>
              <Button variant="outline-primary" onClick={() => setShowCreateDialog(true)}>
                <Cluster gap={4} y="center">
                  {translate('users.actions.add')} <Icon name="Plus" size="xs" />
                </Cluster>
              </Button>
            </Access>
          </Cluster>
        }
        main={
          <Stack gap={16}>
            <Cluster gap={16} y="center">
              <Select
                value={currentRoleFilter}
                onChange={(newRoleFilter) => {
                  setCurrentRoleFilter(newRoleFilter)
                }}
                items={[
                  { value: 'all', label: translate('users.filters.roles.all') },
                  ...roles.map((role) => ({
                    value: role,
                    label: translate(`users.roles.${role}`),
                  })),
                ]}
              />
              <Select
                value={currentOrganizationFilter}
                onChange={(newOrganizationFilter) => {
                  setCurrentOrganizationFilter(newOrganizationFilter)
                }}
                items={[
                  {
                    value: 'all',
                    label: translate('users.filters.organizations.all'),
                  },
                  ...organizations.data.map((organization) => ({
                    value: organization.id,
                    label: organization.name,
                  })),
                ]}
              />
            </Cluster>
            <Stack>
              {!filteredUsers.length ? (
                <Alert title={translate('users.filters.empty')} />
              ) : (
                filteredUsers.map((user) => {
                  const organization = organizations.data.find((org) => org.id === user.organizationId)
                  return (
                    <Card key={user._id}>
                      <Cluster gap={16} x="between">
                        <Cluster gap={16} y="center">
                          <Avatar>
                            <AvatarFallback>
                              {getInitialsForName(`${user?.firstName ?? ''} ${user?.lastName ?? ''}`)}
                            </AvatarFallback>
                          </Avatar>
                          <Stack gap={4}>
                            <Cluster gap={8} y="center">
                              <Typography>
                                {user.firstName} {user.lastName}
                              </Typography>

                              <Typography variant="small" color="muted">
                                {user.email}
                              </Typography>
                              {user.registeredAt ? (
                                <Typography variant="small" color="positive">
                                  ({translate('users.states.active')})
                                </Typography>
                              ) : (
                                <Typography variant="small">({translate('users.states.invited')})</Typography>
                              )}
                            </Cluster>
                            <Cluster gap={4} y="center">
                              <Icon name="Domain" color="muted" size="xs" />
                              <Typography color="muted" variant="small">
                                {organization?.name ?? '?'}
                              </Typography>
                            </Cluster>
                          </Stack>
                        </Cluster>
                        <Cluster gap={8} y="center">
                          <Cluster gap={4}>
                            {user.roles.map((role) => (
                              <Chip key={role} variant="muted">
                                {translate(`users.roles.${role}`)}
                              </Chip>
                            ))}
                          </Cluster>
                          <Access roles={authorize.admins}>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon">
                                  <Icon name="More" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent>
                                {!user.registeredAt ? (
                                  <DropdownMenuItem
                                    onClick={async () => {
                                      await invitationMutation.mutateAsync({
                                        userId: user._id,
                                      })
                                      toaster({
                                        title: translate('users.notifications.resent'),
                                      })
                                    }}
                                  >
                                    {translate('users.list.actions.resend')}
                                  </DropdownMenuItem>
                                ) : null}
                                <DropdownMenuItem onClick={() => setCurrentEditUser(user)}>
                                  {translate('users.list.actions.edit')}
                                </DropdownMenuItem>
                                {authUser?._id !== user._id ? (
                                  <DropdownMenuItem onClick={() => setCurrentDeleteUser(user)}>
                                    {translate('users.list.actions.delete')}
                                  </DropdownMenuItem>
                                ) : null}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </Access>
                        </Cluster>
                      </Cluster>
                    </Card>
                  )
                })
              )}
            </Stack>
          </Stack>
        }
        footer={<Footer />}
      />
      {currentDeleteUser ? (
        <Dialog defaultOpen>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{translate('users.delete.title')}</DialogTitle>
            </DialogHeader>
            <Typography>
              {translate('users.delete.message', {
                email: currentDeleteUser.email,
              })}
            </Typography>
            <DialogFooter>
              <Button variant="outline-primary" onClick={() => setCurrentDeleteUser(undefined)}>
                {translate('users.delete.actions.cancel')}
              </Button>
              <Button
                onClick={async () => {
                  await deleteUserMutation.mutateAsync({
                    userId: currentDeleteUser._id,
                  })
                  setCurrentDeleteUser(undefined)
                }}
              >
                {translate('users.delete.actions.delete')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      ) : null}
      {currentEditUser ? (
        <EditUserDialog userId={currentEditUser._id} onClose={() => setCurrentEditUser(undefined)} />
      ) : null}
      {showCreateDialog ? <CreateUserDialog onClose={() => setShowCreateDialog(false)} /> : null}
    </>
  )
}
